import React, { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import {
  listInboxItems,
  deleteInboxItem,
  bulkDeleteInboxItems,
  restoreInboxItem,
  bulkRestoreInboxItems,
  searchInboxSenders,
} from '../../services/emailInboxApi';
import EmailInboxDetail from './EmailInboxDetail';
import './EmailInbox.css';

const CATEGORIES = [
  { value: '', label: 'All categories' },
  { value: 'order_request', label: 'Order Request' },
  { value: 'quote_request', label: 'Quote Request' },
  { value: 'other', label: 'Other' },
  { value: 'needs_review', label: 'Needs Review' },
  { value: 'blocked', label: 'Blocked' },
];

const STATUSES = [
  { value: '', label: 'All statuses' },
  { value: 'pending', label: 'Pending' },
  { value: 'processing', label: 'Processing' },
  { value: 'processed', label: 'Processed' },
  { value: 'error', label: 'Error' },
  { value: 'blocked', label: 'Blocked' },
];

const CATEGORY_BADGE = {
  order_request: { cls: 'bg-primary', label: 'Order' },
  quote_request: { cls: 'bg-success', label: 'Quote' },
  other: { cls: 'bg-secondary', label: 'Other' },
  needs_review: { cls: 'bg-warning text-dark', label: 'Review' },
  blocked: { cls: 'bg-danger', label: 'Blocked' },
};

const STATUS_BADGE = {
  pending: { cls: 'bg-secondary-subtle text-secondary border border-secondary-subtle', label: 'Pending' },
  processing: { cls: 'bg-info-subtle text-info border border-info-subtle', label: 'Processing' },
  classified: { cls: 'bg-success-subtle text-success border border-success-subtle', label: 'Classified' },
  processed: { cls: 'bg-success-subtle text-success border border-success-subtle', label: 'Processed' },
  error: { cls: 'bg-danger-subtle text-danger border border-danger-subtle', label: 'Error' },
  blocked: { cls: 'bg-danger-subtle text-danger border border-danger-subtle', label: 'Blocked' },
};

function CategoryBadge({ value }) {
  const b = CATEGORY_BADGE[value];
  if (!b) return <span className="badge bg-secondary">{value || '—'}</span>;
  return <span className={`badge ${b.cls}`}>{b.label}</span>;
}

function StatusBadge({ value }) {
  const b = STATUS_BADGE[value];
  if (!b) return <span className="badge bg-secondary">{value || '—'}</span>;
  return <span className={`badge small ${b.cls}`} style={{ fontSize: 11 }}>{b.label}</span>;
}

function initialsOf(item) {
  const name = (item.from_name || item.from_email || '?').trim();
  const parts = name.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).slice(0, 2);
  return name.slice(0, 2);
}

function formatDate(value) {
  if (!value) return '—';
  return new Date(value).toLocaleString(undefined, {
    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

function todayStr() {
  return new Date().toISOString().slice(0, 10);
}

// Server-side typeahead over the cached, deduplicated sender list. Every
// keystroke (debounced) re-queries GET /mailbox/inbox/senders — nothing is
// filtered client-side, so it stays correct even against a huge sender list.
function SenderTypeahead({ selectedEmail, selectedLabel, onSelect, onClear }) {
  const [inputValue, setInputValue] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    setLoading(true);
    const handle = setTimeout(() => {
      searchInboxSenders(inputValue.trim())
        .then((results) => setSuggestions(results))
        .catch(() => setSuggestions([]))
        .finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(handle);
  }, [inputValue, open]);

  if (selectedEmail) {
    return (
      <div
        className="d-flex align-items-center gap-2 border rounded-2 ps-1 pe-2 bg-body-tertiary flex-shrink-0"
        style={{ height: 31 }}
      >
        <div className="eml-avatar eml-avatar-sm">{initialsOf({ from_name: selectedLabel, from_email: selectedEmail })}</div>
        <span className="text-truncate small" style={{ maxWidth: 130 }} title={selectedEmail}>
          {selectedLabel || selectedEmail}
        </span>
        <button
          type="button"
          className="btn btn-sm btn-link p-0 text-muted lh-1"
          onClick={onClear}
          aria-label="Clear sender filter"
        >
          <i className="bi bi-x-lg" />
        </button>
      </div>
    );
  }

  return (
    <div className="position-relative" style={{ minWidth: 190 }}>
      <div className="input-group input-group-sm">
        <span className="input-group-text bg-transparent border-end-0">
          <i className="bi bi-person text-muted" />
        </span>
        <input
          type="text"
          className="form-control border-start-0"
          placeholder="Filter by sender…"
          value={inputValue}
          onFocus={() => setOpen(true)}
          onChange={(e) => { setInputValue(e.target.value); setOpen(true); }}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
        />
      </div>
      {open && (
        <div
          className="eml-sender-dropdown position-absolute top-100 start-0 mt-1 bg-body border rounded-3 shadow-sm w-100"
          style={{ zIndex: 20, maxHeight: 280, overflowY: 'auto', minWidth: 260 }}
        >
          {!loading && suggestions.length > 0 && (
            <div className="eml-sender-dropdown-label px-2 pt-1 pb-1 text-muted text-uppercase">
              {inputValue.trim() ? 'Matching senders' : 'Frequent senders'}
            </div>
          )}
          {loading && (
            <div className="px-3 py-3 small text-muted d-flex align-items-center gap-2">
              <span className="spinner-border spinner-border-sm" />Searching…
            </div>
          )}
          {!loading && suggestions.length === 0 && (
            <div className="px-3 py-3 small text-muted text-center">
              <i className="bi bi-person-x d-block mb-1" style={{ fontSize: 18 }} />
              No senders found.
            </div>
          )}
          {!loading && suggestions.map((s) => (
            <button
              key={s.from_email}
              type="button"
              className="eml-sender-option w-100 text-start border-0 bg-transparent d-flex align-items-center gap-2"
              onMouseDown={(e) => {
                e.preventDefault();
                onSelect(s);
                setInputValue('');
                setOpen(false);
              }}
            >
              <div className="eml-avatar eml-avatar-sm flex-shrink-0">{initialsOf(s)}</div>
              <div className="flex-grow-1 min-width-0">
                <div className="text-truncate small fw-semibold" title={s.from_name || s.from_email}>
                  {s.from_name || s.from_email}
                </div>
                {s.from_name && (
                  <div className="text-truncate text-muted" style={{ fontSize: 11 }} title={s.from_email}>
                    {s.from_email}
                  </div>
                )}
              </div>
              <span className="eml-sender-count flex-shrink-0" title={`${s.email_count} email${s.email_count !== 1 ? 's' : ''}`}>
                {s.email_count}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

const PAGE_SIZE = 25;
const LAYOUT_STORAGE_KEY = 'eml_layout_mode';

function readStoredLayout() {
  try {
    const v = localStorage.getItem(LAYOUT_STORAGE_KEY);
    return v === 'table' || v === 'grid' ? v : 'grid';
  } catch {
    return 'grid';
  }
}

export default function EmailInboxList({ refreshKey }) {
  const [view, setView] = useState('inbox'); // 'inbox' | 'deleted'
  const [layoutMode, setLayoutMode] = useState(readStoredLayout); // 'grid' | 'table'
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [error, setError] = useState('');

  const [filterCategory, setFilterCategory] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterSearch, setFilterSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [filterSender, setFilterSender] = useState('');
  const [filterSenderLabel, setFilterSenderLabel] = useState('');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  const [selectedIds, setSelectedIds] = useState(new Set());
  const [selectedId, setSelectedId] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await listInboxItems({
        category: filterCategory || undefined,
        status: filterStatus || undefined,
        search: filterSearch || undefined,
        sender: filterSender || undefined,
        dateFrom: filterDateFrom || undefined,
        dateTo: filterDateTo || undefined,
        deleted: view === 'deleted',
        page,
        pageSize: PAGE_SIZE,
      });
      setItems(res.items || []);
      setTotal(res.total || 0);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
      setHasLoadedOnce(true);
    }
  }, [filterCategory, filterStatus, filterSearch, filterSender, filterDateFrom, filterDateTo, page, view]);

  useEffect(() => { load(); }, [load, refreshKey]);

  // Debounced search-as-you-type instead of requiring an explicit submit.
  useEffect(() => {
    const handle = setTimeout(() => {
      setFilterSearch(searchInput.trim());
      setPage(1);
    }, 350);
    return () => clearTimeout(handle);
  }, [searchInput]);

  // Clear selection whenever the underlying result set changes.
  useEffect(() => {
    setSelectedIds(new Set());
  }, [view, filterCategory, filterStatus, filterSearch, filterSender, filterDateFrom, filterDateTo, page]);

  function switchView(next) {
    if (next === view) return;
    setView(next);
    setPage(1);
  }

  function switchLayout(next) {
    if (next === layoutMode) return;
    setLayoutMode(next);
    try { localStorage.setItem(LAYOUT_STORAGE_KEY, next); } catch { /* best-effort */ }
  }

  function handleCategoryChange(val) {
    setFilterCategory(val);
    setPage(1);
  }

  function handleStatusChange(val) {
    setFilterStatus(val);
    setPage(1);
  }

  function handleSenderSelect(sender) {
    setFilterSender(sender.from_email);
    setFilterSenderLabel(sender.from_name || sender.from_email);
    setPage(1);
  }

  function handleSenderClear() {
    setFilterSender('');
    setFilterSenderLabel('');
    setPage(1);
  }

  function handleDateFromChange(val) {
    setFilterDateFrom(val);
    setPage(1);
  }

  function handleDateToChange(val) {
    setFilterDateTo(val);
    setPage(1);
  }

  function clearFilters() {
    setFilterCategory('');
    setFilterStatus('');
    setFilterSearch('');
    setSearchInput('');
    setFilterSender('');
    setFilterSenderLabel('');
    setFilterDateFrom('');
    setFilterDateTo('');
    setPage(1);
  }

  const activeFilterCount = [
    filterCategory, filterStatus, filterSearch, filterSender, filterDateFrom, filterDateTo,
  ].filter(Boolean).length;

  function toggleSelect(id) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  function toggleSelectAll() {
    setSelectedIds((prev) => {
      if (prev.size === items.length) return new Set();
      return new Set(items.map((it) => it.id));
    });
  }

  async function handleDeleteOne(id) {
    if (!window.confirm('Delete this email? It will be moved to the Deleted bucket.')) return;
    try {
      await deleteInboxItem(id);
      toast.success('Email moved to Deleted');
      setSelectedId((cur) => (cur === id ? null : cur));
      setItems((prev) => prev.filter((it) => it.id !== id));
      setTotal((t) => Math.max(0, t - 1));
    } catch (e) {
      toast.error(e.message || 'Failed to delete email');
    }
  }

  async function handleRestoreOne(id) {
    try {
      await restoreInboxItem(id);
      toast.success('Email restored to Inbox');
      setItems((prev) => prev.filter((it) => it.id !== id));
      setTotal((t) => Math.max(0, t - 1));
    } catch (e) {
      toast.error(e.message || 'Failed to restore email');
    }
  }

  async function handleBulkDelete() {
    const ids = Array.from(selectedIds);
    if (!ids.length) return;
    if (!window.confirm(`Delete ${ids.length} selected email${ids.length !== 1 ? 's' : ''}? They will be moved to the Deleted bucket.`)) return;
    setBulkBusy(true);
    try {
      await bulkDeleteInboxItems(ids);
      toast.success(`${ids.length} email${ids.length !== 1 ? 's' : ''} moved to Deleted`);
      setSelectedIds(new Set());
      await load();
    } catch (e) {
      toast.error(e.message || 'Failed to delete selected emails');
    } finally {
      setBulkBusy(false);
    }
  }

  async function handleBulkRestore() {
    const ids = Array.from(selectedIds);
    if (!ids.length) return;
    setBulkBusy(true);
    try {
      await bulkRestoreInboxItems(ids);
      toast.success(`${ids.length} email${ids.length !== 1 ? 's' : ''} restored to Inbox`);
      setSelectedIds(new Set());
      await load();
    } catch (e) {
      toast.error(e.message || 'Failed to restore selected emails');
    } finally {
      setBulkBusy(false);
    }
  }

  const totalPages = Math.ceil(total / PAGE_SIZE) || 1;
  // Only blank the whole list for the very first load. Every subsequent
  // refetch (filters, pagination, tab switch, or the inbox's background
  // auto-refresh) keeps showing the current rows and just indicates an
  // update is in flight, instead of flashing to a loading state each time.
  const showInitialLoader = loading && !hasLoadedOnce;
  const allSelected = items.length > 0 && selectedIds.size === items.length;

  const pageWindow = useMemo(() => {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
    const start = Math.max(1, Math.min(page - 3, totalPages - 6));
    return Array.from({ length: 7 }, (_, i) => start + i);
  }, [page, totalPages]);

  return (
    <div>
      {/* Tabs: Inbox / Deleted */}
      <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
        <ul className="nav eml-tabs gap-1">
          <li className="nav-item">
            <button
              type="button"
              className={`nav-link border-0 ${view === 'inbox' ? 'active' : 'text-secondary'}`}
              onClick={() => switchView('inbox')}
            >
              <i className="bi bi-inbox me-1" />Inbox
            </button>
          </li>
          <li className="nav-item">
            <button
              type="button"
              className={`nav-link border-0 ${view === 'deleted' ? 'active' : 'text-secondary'}`}
              onClick={() => switchView('deleted')}
            >
              <i className="bi bi-trash me-1" />Deleted
            </button>
          </li>
        </ul>
        <div className="d-flex align-items-center gap-2">
          <div className="btn-group btn-group-sm" role="group" aria-label="Layout">
            <button
              type="button"
              className={`btn ${layoutMode === 'grid' ? 'btn-primary' : 'btn-outline-secondary'}`}
              onClick={() => switchLayout('grid')}
              title="Grid view"
              aria-pressed={layoutMode === 'grid'}
            >
              <i className="bi bi-grid-3x3-gap-fill" />
            </button>
            <button
              type="button"
              className={`btn ${layoutMode === 'table' ? 'btn-primary' : 'btn-outline-secondary'}`}
              onClick={() => switchLayout('table')}
              title="Table view"
              aria-pressed={layoutMode === 'table'}
            >
              <i className="bi bi-list-ul" />
            </button>
          </div>
          <button type="button" className="btn btn-link btn-sm p-0 text-muted" onClick={load} disabled={loading} title="Refresh">
            {loading
              ? <span className="spinner-border spinner-border-sm" style={{ width: 12, height: 12 }} />
              : <i className="bi bi-arrow-clockwise" />}
          </button>
        </div>
      </div>

      {/* Toolbar: search + filters */}
      <div className="eml-toolbar p-2 p-md-3 mb-3">
        <div className="d-flex flex-wrap align-items-center gap-2">
          <div className="input-group input-group-sm flex-grow-1" style={{ minWidth: 200, maxWidth: 420 }}>
            <span className="input-group-text bg-transparent border-end-0">
              <i className="bi bi-search text-muted" />
            </span>
            <input
              type="text"
              className="form-control border-start-0"
              placeholder="Search subject or sender…"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
            {searchInput && (
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={() => setSearchInput('')}
                aria-label="Clear search"
              >
                <i className="bi bi-x-lg" />
              </button>
            )}
          </div>

          <button
            type="button"
            className="btn btn-sm btn-outline-secondary d-flex align-items-center gap-1 d-md-none"
            onClick={() => setShowFilters((v) => !v)}
          >
            <i className="bi bi-sliders" />Filters
            {activeFilterCount > 0 && (
              <span className="badge bg-primary rounded-pill">{activeFilterCount}</span>
            )}
          </button>

          <div className="d-none d-md-flex align-items-center flex-wrap gap-2 ms-auto">
            <SenderTypeahead
              selectedEmail={filterSender}
              selectedLabel={filterSenderLabel}
              onSelect={handleSenderSelect}
              onClear={handleSenderClear}
            />
            <div className="d-flex align-items-center gap-1">
              <input
                type="date"
                className="form-control form-control-sm"
                style={{ width: 145 }}
                value={filterDateFrom}
                max={filterDateTo || todayStr()}
                onChange={(e) => handleDateFromChange(e.target.value)}
                title="From date"
              />
              <span className="text-muted small">–</span>
              <input
                type="date"
                className="form-control form-control-sm"
                style={{ width: 145 }}
                value={filterDateTo}
                min={filterDateFrom || undefined}
                max={todayStr()}
                onChange={(e) => handleDateToChange(e.target.value)}
                title="To date"
              />
            </div>
            <select
              className="form-select form-select-sm"
              style={{ width: 170 }}
              value={filterCategory}
              onChange={(e) => handleCategoryChange(e.target.value)}
            >
              {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
            <select
              className="form-select form-select-sm"
              style={{ width: 150 }}
              value={filterStatus}
              onChange={(e) => handleStatusChange(e.target.value)}
            >
              {STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
            {activeFilterCount > 0 && (
              <button type="button" className="btn btn-sm btn-link text-decoration-none" onClick={clearFilters}>
                Clear
              </button>
            )}
          </div>
        </div>

        {/* Mobile/tablet filter panel */}
        {showFilters && (
          <div className="d-md-none mt-2 pt-2 border-top d-flex flex-column gap-2">
            <SenderTypeahead
              selectedEmail={filterSender}
              selectedLabel={filterSenderLabel}
              onSelect={handleSenderSelect}
              onClear={handleSenderClear}
            />
            <div className="d-flex align-items-center gap-1">
              <input
                type="date"
                className="form-control form-control-sm"
                value={filterDateFrom}
                max={filterDateTo || todayStr()}
                onChange={(e) => handleDateFromChange(e.target.value)}
                aria-label="From date"
              />
              <span className="text-muted small">–</span>
              <input
                type="date"
                className="form-control form-control-sm"
                value={filterDateTo}
                min={filterDateFrom || undefined}
                max={todayStr()}
                onChange={(e) => handleDateToChange(e.target.value)}
                aria-label="To date"
              />
            </div>
            <select
              className="form-select form-select-sm"
              value={filterCategory}
              onChange={(e) => handleCategoryChange(e.target.value)}
            >
              {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
            <select
              className="form-select form-select-sm"
              value={filterStatus}
              onChange={(e) => handleStatusChange(e.target.value)}
            >
              {STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
            {activeFilterCount > 0 && (
              <button type="button" className="btn btn-sm btn-outline-secondary" onClick={clearFilters}>
                Clear filters
              </button>
            )}
          </div>
        )}
      </div>

      {/* Bulk action bar */}
      {selectedIds.size > 0 && (
        <div className="eml-bulkbar alert alert-primary d-flex align-items-center gap-2 py-2 px-3 mb-3">
          <span className="fw-semibold small">{selectedIds.size} selected</span>
          <button type="button" className="btn btn-sm btn-link text-decoration-none p-0 ms-1" onClick={() => setSelectedIds(new Set())}>
            Clear
          </button>
          <div className="ms-auto d-flex gap-2">
            {view === 'inbox' ? (
              <button type="button" className="btn btn-sm btn-danger" onClick={handleBulkDelete} disabled={bulkBusy}>
                {bulkBusy ? <span className="spinner-border spinner-border-sm me-1" /> : <i className="bi bi-trash me-1" />}
                Delete
              </button>
            ) : (
              <button type="button" className="btn btn-sm btn-outline-primary" onClick={handleBulkRestore} disabled={bulkBusy}>
                {bulkBusy ? <span className="spinner-border spinner-border-sm me-1" /> : <i className="bi bi-arrow-counterclockwise me-1" />}
                Restore
              </button>
            )}
          </div>
        </div>
      )}

      {/* Count + select all (grid mode only — table mode has its own header checkbox) */}
      <div className="d-flex align-items-center justify-content-between mb-2">
        <div className="d-flex align-items-center gap-2">
          {layoutMode === 'grid' && items.length > 0 && (
            <div className="form-check mb-0">
              <input
                type="checkbox"
                className="form-check-input"
                id="eml-select-all"
                checked={allSelected}
                onChange={toggleSelectAll}
              />
              <label className="form-check-label small text-muted" htmlFor="eml-select-all">
                Select all
              </label>
            </div>
          )}
          <span className="small text-muted">
            {total} email{total !== 1 ? 's' : ''}
            {loading && hasLoadedOnce && (
              <span className="ms-2 text-muted"><span className="spinner-border spinner-border-sm" style={{ width: 10, height: 10 }} /> Updating…</span>
            )}
          </span>
        </div>
      </div>

      {error && <div className="alert alert-danger small py-2">{error}</div>}

      {showInitialLoader && (
        <div className="text-center py-5 text-muted">
          <span className="spinner-border spinner-border-sm me-2" />Loading…
        </div>
      )}
      {!showInitialLoader && items.length === 0 && (
        <div className="text-center py-5 text-muted">
          <i className={`bi ${view === 'deleted' ? 'bi-trash' : 'bi-inbox'} me-2`} />
          {view === 'deleted' ? 'No deleted emails.' : 'No emails found.'}
        </div>
      )}

      {/* Grid layout */}
      {!showInitialLoader && items.length > 0 && layoutMode === 'grid' && (
        <div className="eml-grid">
          {items.map((item) => (
            <div
              key={item.id}
              className={`eml-card p-3 ${selectedIds.has(item.id) ? 'eml-card-selected' : ''}`}
              onClick={() => setSelectedId(item.id)}
              role="button"
            >
              <div className="d-flex align-items-start gap-2">
                <input
                  type="checkbox"
                  className="form-check-input mt-1 flex-shrink-0"
                  checked={selectedIds.has(item.id)}
                  onClick={(e) => e.stopPropagation()}
                  onChange={() => toggleSelect(item.id)}
                  aria-label={`Select email from ${item.from_name || item.from_email}`}
                />
                <div className="eml-avatar">{initialsOf(item)}</div>
                <div className="flex-grow-1 min-width-0">
                  <div className="d-flex align-items-center justify-content-between gap-2">
                    <span className="text-truncate fw-semibold small" title={item.from_email}>
                      {item.from_name || item.from_email}
                    </span>
                    <span className="text-muted flex-shrink-0" style={{ fontSize: 11 }}>{formatDate(item.received_at)}</span>
                  </div>
                  <div className="text-truncate small mt-1" title={item.subject}>
                    {item.subject || <span className="text-muted fst-italic">(no subject)</span>}
                  </div>
                  {(item.summary || item.body_preview) && (
                    <div className="text-truncate text-muted mt-1" style={{ fontSize: 11.5 }} title={item.summary || item.body_preview}>
                      {item.summary || item.body_preview}
                    </div>
                  )}
                </div>
              </div>

              <div className="d-flex align-items-center flex-wrap gap-1 mt-2 pt-2 border-top">
                <CategoryBadge value={item.category} />
                <StatusBadge value={item.status} />
                {item.has_attachments && <i className="bi bi-paperclip text-muted ms-1" title="Has attachments" />}
                {item.og_request_ids?.slice(0, 1).map((ogId) => (
                  <a
                    key={ogId}
                    href={`/order-x-v4?openRequest=${item.og_request_codes?.[0] || ogId}`}
                    className="badge bg-primary text-white text-decoration-none"
                    style={{ fontSize: 10 }}
                    title={`Open OX Request #${item.og_request_seqs?.[0] ?? ogId}`}
                    onClick={(e) => e.stopPropagation()}
                  >
                    OX-{item.og_request_seqs?.[0] ?? ogId}
                  </a>
                ))}
                <div className="ms-auto d-flex gap-1">
                  {view === 'inbox' ? (
                    <button
                      type="button"
                      className="btn btn-sm eml-icon-btn text-danger"
                      title="Delete"
                      onClick={(e) => { e.stopPropagation(); handleDeleteOne(item.id); }}
                    >
                      <i className="bi bi-trash" />
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="btn btn-sm eml-icon-btn text-primary"
                      title="Restore"
                      onClick={(e) => { e.stopPropagation(); handleRestoreOne(item.id); }}
                    >
                      <i className="bi bi-arrow-counterclockwise" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Table layout */}
      {!showInitialLoader && items.length > 0 && layoutMode === 'table' && (
        <div className="table-responsive border rounded-3">
          <table className="table table-hover table-sm mb-0 small align-middle">
            <thead className="table-light">
              <tr>
                <th style={{ width: 36 }}>
                  <input
                    type="checkbox"
                    className="form-check-input"
                    checked={allSelected}
                    onChange={toggleSelectAll}
                    aria-label="Select all"
                  />
                </th>
                <th>From</th>
                <th>Subject</th>
                <th>Summary</th>
                <th>Received</th>
                <th>Category</th>
                <th>Status</th>
                <th className="text-center">Att.</th>
                <th className="text-center">OX</th>
                <th style={{ width: 90 }} />
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr
                  key={item.id}
                  className={`eml-row ${selectedIds.has(item.id) ? 'eml-row-selected' : ''}`}
                  onClick={() => setSelectedId(item.id)}
                >
                  <td onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      className="form-check-input"
                      checked={selectedIds.has(item.id)}
                      onChange={() => toggleSelect(item.id)}
                      aria-label={`Select email from ${item.from_name || item.from_email}`}
                    />
                  </td>
                  <td>
                    <span className="text-truncate d-inline-block" style={{ maxWidth: 160 }} title={item.from_email}>
                      {item.from_name || item.from_email}
                    </span>
                  </td>
                  <td>
                    <span className="text-truncate d-inline-block" style={{ maxWidth: 260 }} title={item.subject}>
                      {item.subject || <span className="text-muted fst-italic">(no subject)</span>}
                    </span>
                  </td>
                  <td>
                    <span className="text-truncate d-inline-block text-muted" style={{ maxWidth: 260, fontSize: 12.5 }} title={item.summary}>
                      {item.summary || '—'}
                    </span>
                  </td>
                  <td className="text-nowrap text-muted">{formatDate(item.received_at)}</td>
                  <td><CategoryBadge value={item.category} /></td>
                  <td><StatusBadge value={item.status} /></td>
                  <td className="text-center">
                    {item.has_attachments
                      ? <i className="bi bi-paperclip text-muted" title="Has attachments" />
                      : null}
                  </td>
                  <td className="text-center">
                    {item.og_request_ids?.length > 0 && (
                      <div className="d-flex flex-wrap gap-1 justify-content-center">
                        {item.og_request_ids.map((ogId, idx) => (
                          <a
                            key={ogId}
                            href={`/order-x-v4?openRequest=${item.og_request_codes?.[idx] || ogId}`}
                            className="badge bg-primary text-white text-decoration-none"
                            style={{ fontSize: 10 }}
                            title={`Open OX Request #${item.og_request_seqs?.[idx] ?? ogId}`}
                            onClick={(e) => e.stopPropagation()}
                          >
                            OX-{item.og_request_seqs?.[idx] ?? ogId}
                          </a>
                        ))}
                      </div>
                    )}
                  </td>
                  <td onClick={(e) => e.stopPropagation()}>
                    <div className="d-flex align-items-center gap-1">
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary py-0 px-2"
                        style={{ fontSize: 11 }}
                        onClick={() => setSelectedId(item.id)}
                      >
                        View
                      </button>
                      {view === 'inbox' ? (
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger py-0 px-2"
                          title="Delete"
                          onClick={() => handleDeleteOne(item.id)}
                        >
                          <i className="bi bi-trash" />
                        </button>
                      ) : (
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-primary py-0 px-2"
                          title="Restore"
                          onClick={() => handleRestoreOne(item.id)}
                        >
                          <i className="bi bi-arrow-counterclockwise" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="d-flex align-items-center justify-content-between mt-3">
          <span className="small text-muted">
            Page {page} of {totalPages}
          </span>
          <nav aria-label="Inbox pagination">
            <ul className="pagination pagination-sm mb-0">
              <li className={`page-item ${page === 1 ? 'disabled' : ''}`}>
                <button className="page-link" onClick={() => setPage(page - 1)} disabled={page === 1}>
                  <i className="bi bi-chevron-left" />
                </button>
              </li>
              {pageWindow.map((p) => (
                <li key={p} className={`page-item ${p === page ? 'active' : ''}`}>
                  <button className="page-link" onClick={() => setPage(p)}>{p}</button>
                </li>
              ))}
              <li className={`page-item ${page === totalPages ? 'disabled' : ''}`}>
                <button className="page-link" onClick={() => setPage(page + 1)} disabled={page === totalPages}>
                  <i className="bi bi-chevron-right" />
                </button>
              </li>
            </ul>
          </nav>
        </div>
      )}

      {/* Detail modal */}
      {selectedId && (
        <EmailInboxDetail
          itemId={selectedId}
          isDeletedView={view === 'deleted'}
          onClose={() => setSelectedId(null)}
          onDeleted={(id) => {
            setSelectedId(null);
            setItems((prev) => prev.filter((it) => it.id !== id));
            setTotal((t) => Math.max(0, t - 1));
          }}
          onRestored={(id) => {
            setSelectedId(null);
            setItems((prev) => prev.filter((it) => it.id !== id));
            setTotal((t) => Math.max(0, t - 1));
          }}
        />
      )}
    </div>
  );
}
